import { useAuth } from '@hooks/useAuth';
import { Product } from '@interfaces';
import {
  getPillBoxDetail,
  GetPillBoxDetailQueryParams,
  GetPillBoxDetailResponseQuery,
} from '@queries/pillBox/queries';
import { ProductMainCard, Title } from '../styles';
import { useMutation, useQuery } from '@apollo/client';

import Image from 'next/image';
import { MenuItem, Skeleton } from '@mui/material';
import { daysUntil, extractMiddleDate } from '@utils/date';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useRef } from 'react';
import {
  UpdatePillBoxDetailMutation,
  UpdatePillBoxDetailMutationParams,
  UpdatePillBoxDetailResponse,
} from '@queries/pillBox/mutation/updatePillBoxDetail';
import { useDebouncedCallback } from '@hooks/useDebouncedCallback';
import {
  DosageInput,
  FrequencyInput,
  InlineSkeleton,
  InputsContainer,
  InputsRow,
  InputsTitle,
  RemainingChipContainer,
  RemainingChipIcon,
  RemainingChipText,
  RemainingContainer,
  RemainingTitle,
  StyledCheckIcon,
  StyledPriorityHighIcon,
  SubTitle,
  TitleContainer,
} from './PillBox.styles';
import { useSegment } from '@hooks/useSegment';

const PillBoxSkeleton = () => {
  return (
    <ProductMainCard>
      <TitleContainer>
        <Skeleton variant="rectangular" width={40} height={40} />
        <div>
          <Skeleton variant="rectangular" width={130} height={21} />
          <Skeleton variant="rectangular" width={186} height={21} />
        </div>
      </TitleContainer>
    </ProductMainCard>
  );
};

const icons = {
  check: <StyledCheckIcon />,
  warning: <StyledPriorityHighIcon />,
};

interface PillBoxProps {
  product: Product;
}
export const PillBox = (props: PillBoxProps) => {
  const { appUser, loading: authLoading } = useAuth();
  const { track } = useSegment();
  const loadedRef = useRef(false);
  const { data: { pillBoxDetail } = {}, loading } = useQuery<
    GetPillBoxDetailResponseQuery,
    GetPillBoxDetailQueryParams
  >(getPillBoxDetail(), {
    variables: {
      params: { productSlug: props.product?.slug },
    },
    skip: !props.product?.slug || !appUser,
  });

  const [updatePillBoxDetail, { loading: updating }] = useMutation<
    UpdatePillBoxDetailResponse,
    UpdatePillBoxDetailMutationParams
  >(UpdatePillBoxDetailMutation());
  const { register, setValue, watch, formState, reset } = useForm({
    defaultValues: {
      frequency: pillBoxDetail?.frequency ?? 0,
      consumptionUnit: pillBoxDetail?.consumptionUnit ?? 0,
    },
    mode: 'onChange',
  });
  register('consumptionUnit');
  register('frequency');
  const values = watch();
  const existPillBoxDetail = Boolean(pillBoxDetail);
  useEffect(
    function loadInitialPillboxDetailToForm() {
      if (loading || !existPillBoxDetail) return;
      if (loadedRef.current) return;
      loadedRef.current = true;
      reset({
        frequency: pillBoxDetail.frequency,
        consumptionUnit: pillBoxDetail.consumptionUnit,
      });
    },
    [
      loading,
      pillBoxDetail?.consumptionUnit,
      pillBoxDetail?.frequency,
      existPillBoxDetail,
      reset,
    ],
  );
  const update = useCallback(
    (data: UpdatePillBoxDetailMutationParams) => {
      track('pillbox consumption change');
      return updatePillBoxDetail({
        variables: data,
      }).then(() => {
        reset({
          frequency: data.params.frequency,
          consumptionUnit: data.params.consumptionUnit,
        });
      });
    },
    [updatePillBoxDetail, reset, track],
  );
  const debouncedUpdate = useDebouncedCallback(update, 300);
  useEffect(
    function handlePillboxUpdate() {
      if (!formState.isDirty) return;
      void debouncedUpdate({
        params: {
          productSlug: props.product?.slug,
          consumptionUnit: values.consumptionUnit,
          frequency: values.frequency,
        },
      });
    },
    [
      debouncedUpdate,
      props.product?.slug,
      values.consumptionUnit,
      values.frequency,
      formState.isDirty,
    ],
  );
  if (loading || updating || authLoading) return <PillBoxSkeleton />;
  if (!pillBoxDetail) return null;

  const isSafeUnit = Boolean(
    /(comprimido|capsula|cápsula)/.exec(props.product.unit),
  );
  const pillBoxTagType =
    pillBoxDetail.remainingBalanceDays <= 5 ? 'warning' : 'check';
  const lastOrderDate = new Date(pillBoxDetail.lastOrderDate);
  const daysFromLastPurchase = daysUntil(new Date(Date.now()), lastOrderDate);
  const lastPurchaseText =
    daysFromLastPurchase > 30
      ? `Comprado hace más de ${Math.trunc(daysFromLastPurchase / 30)} mes${
          daysFromLastPurchase >= 60 ? 'es' : ''
        }`
      : `Última compra: ${extractMiddleDate(lastOrderDate)}`;
  return (
    <ProductMainCard>
      <TitleContainer>
        <Image
          alt="PillboxLogo"
          src={'/assets/images/pillbox-logo.webp'}
          width={40}
          height={40}
        />
        {isSafeUnit ? (
          <div>
            <Title variant="h2">Tu tratamiento</Title>
            <SubTitle>
              Última compra: {extractMiddleDate(lastOrderDate)}
            </SubTitle>
          </div>
        ) : (
          <div>
            <Title variant="h2">Última compra</Title>
            <SubTitle>{lastPurchaseText}</SubTitle>
          </div>
        )}
      </TitleContainer>
      {isSafeUnit && (
        <>
          <InputsContainer>
            <InputsTitle>Ingresa tu dosis indicada</InputsTitle>
            <InputsRow>
              <DosageInput
                label="Dosis"
                select
                value={values.consumptionUnit}
                onChange={(e) =>
                  setValue('consumptionUnit', +e.target.value, {
                    shouldDirty: true,
                  })
                }
              >
                <MenuItem value={0.5}>1/2 comprimido</MenuItem>
                <MenuItem value={1}>1 comprimido</MenuItem>
                <MenuItem value={1.5}>1 y 1/2 comprimidos</MenuItem>
                <MenuItem value={2}>2 comprimidos</MenuItem>
                <MenuItem value={3}>3 comprimidos</MenuItem>
                <MenuItem value={4}>4 comprimidos</MenuItem>
                <MenuItem value={5}>5 comprimidos</MenuItem>
              </DosageInput>
              <FrequencyInput
                label="Frecuencia"
                select
                value={values.frequency}
                onChange={(e) =>
                  setValue('frequency', +e.target.value, { shouldDirty: true })
                }
              >
                <MenuItem value={1}>al día</MenuItem>
                <MenuItem value={7}>a la semana</MenuItem>
              </FrequencyInput>
            </InputsRow>
          </InputsContainer>
          <RemainingContainer>
            <RemainingTitle>
              Te alcanza para{' '}
              {updating || loading ? (
                <InlineSkeleton variant="rectangular" width={30} />
              ) : (
                pillBoxDetail.remainingBalanceDays
              )}{' '}
              días más
            </RemainingTitle>
            {updating || loading ? (
              <Skeleton variant="rectangular" width={200} />
            ) : (
              <RemainingChipContainer type={pillBoxTagType}>
                <RemainingChipIcon type={pillBoxTagType}>
                  {icons[pillBoxTagType]}
                </RemainingChipIcon>
                <RemainingChipText type={pillBoxTagType}>
                  {pillBoxDetail.userStock} comprimidos restantes{' '}
                </RemainingChipText>
              </RemainingChipContainer>
            )}
          </RemainingContainer>
        </>
      )}
    </ProductMainCard>
  );
};
